
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import ChatWidget from '../../home/components/ChatWidget';

const NetworkingPage: React.FC = () => {
  const location = useLocation();
  const isNL = location.pathname.startsWith('/nl');
  const prefix = isNL ? '/nl' : '';

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [sortBy, setSortBy] = useState('featured');

  const networkingProducts = [
    {
      id: 1,
      name: 'Cisco Catalyst 9300 Series',
      category: isNL ? 'Enterprise Switch' : 'Enterprise Switch',
      image: 'https://public.readdy.ai/ai/img_res/0a93cbb0054eb26d842510ea78355975.jpg',
      description: isNL
        ? 'Hoogwaardige enterprise-klasse switch met geavanceerd netwerkbeheer, PoE+ ondersteuning en cloudgebaseerd beheer voor veeleisende bedrijfsomgevingen.'
        : 'High-performance enterprise-class switch with advanced network management, PoE+ support and cloud-based management for demanding business environments.',
      specs: {
        [isNL ? 'Poorten' : 'Ports']: '48',
        [isNL ? 'Snelheid' : 'Speed']: '10 Gigabit',
        [isNL ? 'PoE' : 'PoE']: isNL ? 'PoE+ Ondersteuning' : 'PoE+ Support',
        [isNL ? 'Beheer' : 'Management']: isNL ? 'Cloudbeheer' : 'Cloud Managed'
      },
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green'
    },
    {
      id: 2,
      name: 'Ubiquiti UniFi Dream Machine Pro',
      category: isNL ? 'Beveiligingsgateway' : 'Security Gateway',
      image: 'https://readdy.ai/api/search-image?query=modern%20ubiquiti%20unifi%20dream%20machine%20pro%20network%20security%20gateway%20device%20with%20sleek%20black%20design%20and%20LED%20display%20in%20professional%20setting%20with%20simple%20background&width=400&height=400&seq=netprod2&orientation=squarish',
      description: isNL
        ? 'Alles-in-één beveiligingsgateway met ingebouwde IDS/IPS, VPN-server en netwerkbeheer voor kleine tot middelgrote bedrijven.'
        : 'All-in-one security gateway with built-in IDS/IPS, VPN server and network management for small to medium businesses.',
      specs: {
        [isNL ? 'Doorvoer' : 'Throughput']: '3.5 Gbps',
        [isNL ? 'Poorten' : 'Ports']: '8 x 1GbE + 2 x 10GbE',
        [isNL ? 'Opslag' : 'Storage']: '3.5" HDD Bay',
        [isNL ? 'Functies' : 'Features']: 'IDS/IPS, VPN'
      },
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green'
    },
    {
      id: 3,
      name: 'Cisco Meraki MR46 Access Point',
      category: isNL ? 'Draadloos Toegangspunt' : 'Wireless Access Point',
      image: 'https://readdy.ai/api/search-image?query=cisco%20meraki%20wireless%20access%20point%20mounted%20on%20ceiling%20in%20modern%20office%20environment%20with%20clean%20white%20design%20and%20professional%20lighting%20simple%20background&width=400&height=400&seq=netprod3&orientation=squarish',
      description: isNL
        ? 'WiFi 6 toegangspunt met cloudgebaseerd beheer, ondersteuning voor 512 gelijktijdige clients en geavanceerde beveiligingsfuncties.'
        : 'WiFi 6 access point with cloud-based management, support for 512 simultaneous clients and advanced security features.',
      specs: {
        [isNL ? 'Standaard' : 'Standard']: 'WiFi 6 (802.11ax)',
        [isNL ? 'Snelheid' : 'Speed']: '3 Gbps',
        [isNL ? 'Clients' : 'Clients']: '512',
        [isNL ? 'Beheer' : 'Management']: isNL ? 'Cloudbeheer' : 'Cloud Managed'
      },
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green'
    },
    {
      id: 4,
      name: 'Fortinet FortiGate 100F',
      category: isNL ? 'Next-Gen Firewall' : 'Next-Gen Firewall',
      image: 'https://readdy.ai/api/search-image?query=fortinet%20fortigate%20enterprise%20firewall%20appliance%20with%20red%20accents%20in%20professional%20data%20center%20rack%20mount%20configuration%20with%20simple%20clean%20background&width=400&height=400&seq=netprod4&orientation=squarish',
      description: isNL
        ? 'Geavanceerde next-generation firewall met geïntegreerde dreigingspreventie, SSL-inspectie en hoge doorvoersnelheden voor enterprise-beveiliging.'
        : 'Advanced next-generation firewall with integrated threat prevention, SSL inspection and high throughput speeds for enterprise security.',
      specs: {
        [isNL ? 'Doorvoer' : 'Throughput']: '10 Gbps',
        [isNL ? 'VPN' : 'VPN']: '2 Gbps IPSec',
        [isNL ? 'Poorten' : 'Ports']: '16 x GbE + 2 x 10GbE',
        [isNL ? 'Bescherming' : 'Protection']: isNL ? 'Geavanceerde Dreigingspreventie' : 'Advanced Threat'
      },
      availability: isNL ? 'Voorbestelling' : 'Pre-Order',
      availabilityColor: 'yellow'
    },
    {
      id: 5,
      name: 'Aruba 6300M Switch Series',
      category: isNL ? 'Core Switch' : 'Core Switch',
      image: 'https://readdy.ai/api/search-image?query=aruba%20enterprise%20core%20network%20switch%20with%20orange%20accents%20and%20multiple%20fiber%20optic%20ports%20in%20modern%20data%20center%20with%20clean%20professional%20background&width=400&height=400&seq=netprod5&orientation=squarish',
      description: isNL
        ? 'Enterprise core switch met hoge schakelcapaciteit, dubbele voeding en ondersteuning voor 25/10 Gigabit verbindingen.'
        : 'Enterprise core switch with high switching capacity, dual power supply and support for 25/10 Gigabit connections.',
      specs: {
        [isNL ? 'Poorten' : 'Ports']: '48',
        [isNL ? 'Snelheid' : 'Speed']: '25/10 Gigabit',
        [isNL ? 'Schakelcapaciteit' : 'Switching']: '2.88 Tbps',
        [isNL ? 'Redundantie' : 'Redundancy']: isNL ? 'Dubbele Voeding' : 'Dual PSU'
      },
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green'
    },
    {
      id: 6,
      name: 'Juniper EX4300 Series',
      category: isNL ? 'Toegangsswitch' : 'Access Switch',
      image: 'https://readdy.ai/api/search-image?query=juniper%20networks%20enterprise%20access%20switch%20with%20multiple%20ethernet%20ports%20in%20professional%20rack%20mount%20configuration%20with%20simple%20clean%20background&width=400&height=400&seq=netprod6&orientation=squarish',
      description: isNL
        ? 'Betrouwbare toegangsswitch met 48 poorten, 740W PoE+ en Virtual Chassis stacking voor flexibele netwerkuitbreiding.'
        : 'Reliable access switch with 48 ports, 740W PoE+ and Virtual Chassis stacking for flexible network expansion.',
      specs: {
        [isNL ? 'Poorten' : 'Ports']: '48 x 1GbE',
        [isNL ? 'Uplink' : 'Uplink']: '4 x 10GbE',
        [isNL ? 'PoE' : 'PoE']: '740W PoE+',
        [isNL ? 'Stacking' : 'Stacking']: 'Virtual Chassis'
      },
      availability: isNL ? 'Aangepaste Configuratie' : 'Custom Config',
      availabilityColor: 'blue'
    }
  ];

  const productNames: Record<number, string> = {
    1: 'Cisco Catalyst 9300 Series',
    2: 'Ubiquiti UniFi Dream Machine Pro',
    3: 'Cisco Meraki MR46 Access Point',
    4: 'Fortinet FortiGate 100F',
    5: 'Aruba 6300M Switch Series',
    6: 'Juniper EX4300 Series',
  };

  const toggleProductSelection = (productId: number) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else if (selectedProducts.length < 4) {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center text-sm text-gray-600">
            <a href={prefix || '/'} className="hover:text-primary cursor-pointer flex items-center">
              <div className="w-4 h-4 flex items-center justify-center mr-1">
                <i className="ri-home-line"></i>
              </div>
              Home
            </a>
            <div className="w-4 h-4 flex items-center justify-center mx-2">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <a href="#" className="hover:text-primary cursor-pointer">Hardware</a>
            <div className="w-4 h-4 flex items-center justify-center mx-2">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-gray-900 font-medium">{isNL ? 'Netwerkapparatuur' : 'Networking'}</span>
          </div>
        </div>
      </div>

      {/* Page Header */}
      <section className="py-12 bg-gradient-to-r from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {isNL ? 'Professionele Netwerkapparatuur' : 'Professional Networking Equipment'}
          </h1>
          <p className="text-lg text-gray-600 max-w-4xl">
            {isNL
              ? 'Enterprise-grade netwerkoplossingen inclusief switches, routers, draadloze toegangspunten en beveiligingsapparaten. Bouw een robuuste, schaalbare en veilige netwerkinfrastructuur met toonaangevende merken en geavanceerde technologie.'
              : 'Enterprise-grade networking solutions including switches, routers, wireless access points, and security appliances. Build robust, scalable, and secure network infrastructure with industry-leading brands and cutting-edge technology.'}
          </p>
        </div>
      </section>

      {/* Leading Networking Brands */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            {isNL ? 'Toonaangevende Netwerkmerken' : 'Leading Networking Brands'}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { name: 'Cisco', icon: 'ri-router-line', color: 'blue' },
              { name: 'Fortinet', icon: 'ri-shield-check-line', color: 'red' },
              { name: 'Aruba Networks', icon: 'ri-wifi-line', color: 'orange' },
              { name: 'Juniper', icon: 'ri-server-line', color: 'green' }
            ].map((brand, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg p-6 text-center hover:shadow-md transition-shadow cursor-pointer">
                <div className={`w-16 h-16 bg-${brand.color}-100 rounded-lg flex items-center justify-center mx-auto mb-4`}>
                  <div className={`w-8 h-8 flex items-center justify-center text-${brand.color}-600`}>
                    <i className={`${brand.icon} text-3xl`}></i>
                  </div>
                </div>
                <h3 className="font-semibold text-gray-900">{brand.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="flex gap-8">
            {/* Filters Sidebar */}
            <div className="w-64 flex-shrink-0">
              <div className="bg-white rounded-lg border border-gray-200 p-6 sticky top-4">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-bold">{isNL ? 'Filters' : 'Filters'}</h3>
                  <button className="text-primary text-sm hover:underline cursor-pointer">
                    {isNL ? 'Alles Wissen' : 'Reset Filters'}
                  </button>
                </div>

                {/* Product Category */}
                <div className="mb-6">
                  <h4 className="font-semibold text-sm text-gray-900 mb-3">
                    {isNL ? 'Productcategorie' : 'Product Category'}
                  </h4>
                  <div className="space-y-2">
                    {(isNL
                      ? ['Switches', 'Routers', 'Draadloos', 'Firewalls', 'Toegangspunten']
                      : ['Switches', 'Routers', 'Wireless', 'Firewalls', 'Access Points']
                    ).map((cat, idx) => (
                      <label key={idx} className="flex items-center cursor-pointer">
                        <input type="checkbox" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer" />
                        <span className="ml-2 text-sm text-gray-700">{cat}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Brand */}
                <div className="mb-6">
                  <h4 className="font-semibold text-sm text-gray-900 mb-3">
                    {isNL ? 'Merk' : 'Brand'}
                  </h4>
                  <div className="space-y-2">
                    {['Cisco', 'Fortinet', 'Aruba', 'Juniper', 'Ubiquiti', 'Meraki'].map((brand, idx) => (
                      <label key={idx} className="flex items-center cursor-pointer">
                        <input type="checkbox" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer" />
                        <span className="ml-2 text-sm text-gray-700">{brand}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Port Count */}
                <div className="mb-6">
                  <h4 className="font-semibold text-sm text-gray-900 mb-3">
                    {isNL ? 'Aantal Poorten' : 'Port Count'}
                  </h4>
                  <div className="space-y-2">
                    {(isNL
                      ? ['8 Poorten', '16 Poorten', '24 Poorten', '48 Poorten', '48+ Poorten']
                      : ['8 Ports', '16 Ports', '24 Ports', '48 Ports', '48+ Ports']
                    ).map((port, idx) => (
                      <label key={idx} className="flex items-center cursor-pointer">
                        <input type="checkbox" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer" />
                        <span className="ml-2 text-sm text-gray-700">{port}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Speed */}
                <div className="mb-6">
                  <h4 className="font-semibold text-sm text-gray-900 mb-3">
                    {isNL ? 'Netwerksnelheid' : 'Network Speed'}
                  </h4>
                  <div className="space-y-2">
                    {['1 Gigabit', '10 Gigabit', '25 Gigabit', '40 Gigabit', '100 Gigabit'].map((speed, idx) => (
                      <label key={idx} className="flex items-center cursor-pointer">
                        <input type="checkbox" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer" />
                        <span className="ml-2 text-sm text-gray-700">{speed}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Availability */}
                <div className="mb-6">
                  <h4 className="font-semibold text-sm text-gray-900 mb-3">
                    {isNL ? 'Beschikbaarheid' : 'Availability'}
                  </h4>
                  <div className="space-y-2">
                    <label className="flex items-center cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer" />
                      <span className="ml-2 text-sm text-gray-700">{isNL ? 'Op Voorraad' : 'In Stock'}</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer" />
                      <span className="ml-2 text-sm text-gray-700">{isNL ? 'Voorbestelling' : 'Pre-Order'}</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer" />
                      <span className="ml-2 text-sm text-gray-700">{isNL ? 'Aangepaste Configuratie' : 'Custom Config'}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">
                    {isNL
                      ? `${networkingProducts.length} Producten`
                      : `${networkingProducts.length} Products`}
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-600 mr-1">{isNL ? 'Weergave:' : 'View:'}</span>
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded cursor-pointer ${viewMode === 'grid' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'}`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-grid-line"></i>
                      </div>
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded cursor-pointer ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'}`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-list-check"></i>
                      </div>
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">{isNL ? 'Sorteren op:' : 'Sort by:'}</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary cursor-pointer"
                  >
                    <option value="featured">{isNL ? 'Aanbevolen' : 'Featured'}</option>
                    <option value="price-low">{isNL ? 'Prijs: Laag naar Hoog' : 'Price: Low to High'}</option>
                    <option value="price-high">{isNL ? 'Prijs: Hoog naar Laag' : 'Price: High to Low'}</option>
                    <option value="name">{isNL ? 'Naam: A tot Z' : 'Name: A to Z'}</option>
                    <option value="popularity">{isNL ? 'Populariteit' : 'Popularity'}</option>
                    <option value="newest">{isNL ? 'Nieuwste Eerst' : 'Newest First'}</option>
                  </select>
                </div>
              </div>

              {/* Products */}
              <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'} data-product-shop>
                {networkingProducts.map((product) => (
                  <div
                    key={product.id}
                    className={`bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-shadow ${
                      viewMode === 'list' ? 'flex gap-6' : ''
                    }`}
                  >
                    <div className={viewMode === 'list' ? 'w-48 flex-shrink-0' : ''}>
                      <div className={`bg-gray-50 relative ${viewMode === 'list' ? 'rounded-l-lg h-full' : 'rounded-t-lg h-64'} flex items-center justify-center p-4`}>
                        <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                        <div className="absolute top-3 left-3">
                          <span className={`bg-${product.availabilityColor}-100 text-${product.availabilityColor}-800 text-xs font-medium px-2 py-1 rounded-full`}>
                            {product.availability}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={`p-6 ${viewMode === 'list' ? 'flex-1' : ''}`}>
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-xs font-medium text-primary bg-blue-50 px-2 py-1 rounded">{product.category}</span>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedProducts.includes(product.id)}
                            onChange={() => toggleProductSelection(product.id)}
                            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                          />
                          <span className="ml-2 text-xs text-gray-600">{isNL ? 'Vergelijken' : 'Compare'}</span>
                        </label>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{product.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">{product.description}</p>
                      <div className="space-y-1 mb-4">
                        {Object.entries(product.specs).map(([key, value], idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-gray-600">{key}:</span>
                            <span className="font-medium text-gray-900">{value}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div>
                          <div className={`text-xs text-${product.availabilityColor}-600 font-medium`}>{product.availability}</div>
                        </div>
                        <a href={`${prefix}/contact`} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors whitespace-nowrap cursor-pointer">
                          {isNL ? 'Offerte Aanvragen' : 'Request a Quote'}
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-12">
                <div className="text-gray-600 text-sm">
                  {isNL ? '1-6 van 48 resultaten weergegeven' : 'Showing 1-6 of 48 results'}
                </div>
                <div className="flex items-center gap-2">
                  <button className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-50" disabled>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line"></i>
                    </div>
                  </button>
                  <button className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium cursor-pointer">1</button>
                  <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer">2</button>
                  <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer">3</button>
                  <span className="px-2 text-gray-500">...</span>
                  <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer">8</button>
                  <button className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg transition-transform duration-300 z-40 ${
          selectedProducts.length > 0 ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-900">
                {isNL ? 'Producten Vergelijken' : 'Compare Products'} ({selectedProducts.length}/4)
              </span>
              <div className="flex gap-2">
                {selectedProducts.map((id) => (
                  <div key={id} className="bg-gray-100 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                    <span>{productNames[id] || `Product ${id}`}</span>
                    <button
                      onClick={() => toggleProductSelection(id)}
                      className="text-gray-500 hover:text-gray-700 cursor-pointer"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-close-line"></i>
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setSelectedProducts([])}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer"
              >
                {isNL ? 'Alles Wissen' : 'Clear All'}
              </button>
              <button className="bg-primary text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors whitespace-nowrap cursor-pointer">
                {isNL ? 'Selectie Vergelijken' : 'Compare Now'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default NetworkingPage;
